"use client";

import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from 'react-i18next';
import { useChatContext } from '@/contexts/ChatContext';

interface QuickActionButtonsProps {
  onSelect: (text: string) => void;
  onOpenLastConversation?: () => void;
}


// Module-level cache to persist randomization across unmounts/remounts
let cachedKeys: { linkKey: string | undefined; actionKeys: string[]; purpleActionKey: string | undefined } | null = null;

const QuickActionButtonsComponent: React.FC<QuickActionButtonsProps> = ({ onSelect, onOpenLastConversation }) => {
  const { t } = useTranslation();
  const { chatSessions, userId } = useChatContext();

  // Optimistically check for past conversations to prevent layout shift/flash
  // If chatSessions is empty (loading), check localStorage directly
  const hasPastConversations = useMemo(() => {
    if (chatSessions && chatSessions.length > 0) return true;

    // If no sessions in context yet, check cache if we have a userId
    if (typeof window !== 'undefined' && userId) {
      try {
        const cacheKey = `dify-conversations-${userId}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          return Array.isArray(parsed) && parsed.length > 0;
        }
      } catch (e) {
        // Ignore errors
      }
    }
    return false;
  }, [chatSessions, userId]);
  type ActionItem =
    | { key: string; kind: 'action'; label: string }
    | { key: string; kind: 'link'; label: string; href: string }
    | { key: string; kind: 'open-last'; label: string };


  const isMobile = useIsMobile();

  // Build the pool of possible quick actions
  const pool: ActionItem[] = useMemo(() => {
    const sponsorDealsLabel = t('chat.quick_actions.lead_search');
    const recommendations = [
      t('chat.quick_actions.engagement_rate'),
      t('chat.quick_actions.create_video'),
      t('chat.quick_actions.most_viewed_post'),
      t('chat.quick_actions.evaluate_profile'),
      t('chat.quick_actions.next_video_type'),
      t('chat.quick_actions.grow_followers'),
      t('chat.quick_actions.tutorial'),
      t('chat.quick_actions.create_content_plan'),
      t('chat.quick_actions.compare_highest_lowest'),
      t('chat.quick_actions.optimize_bio'),
    ];

    const actionItems: ActionItem[] = [
      // Always-available action (grey button)
      // ({ key: 'act-find-sponsor-deals', kind: 'action', label: sponsorDealsLabel } as const) as ActionItem,
      // Rotating recommendations
      ...recommendations.map((r, idx) => (
        ({ key: `rec-${idx}`, kind: 'action', label: r } as const) as ActionItem
      )),
    ];

    const linkItems: ActionItem[] = [
      // { key: 'link-search', kind: 'link', label: t('chat.quick_actions.collab_of_the_week'), href: '/brand-search' },
      // { key: 'link-portfolio', kind: 'link', label: t('chat.quick_actions.create_portfolio'), href: '/portfolio' },
    ];

    return [...actionItems, ...linkItems];
  }, [t]);

  // Randomize items once (stable across re-renders and remounts)
  const [randomizedItems, setRandomizedItems] = useState<{
    link: ActionItem | undefined;
    actions: ActionItem[];
    purpleActionKey: string | undefined;
  }>(() => {
    const linkItems = pool.filter(i => i.kind === 'link');
    const actionItems = pool.filter(i => i.kind === 'action' && i.key !== 'act-find-sponsor-deals');

    // If we have cached keys, try to use them to reconstruct the items
    const currentCachedKeys = cachedKeys;
    if (currentCachedKeys) {
      const link = linkItems.find(i => i.key === currentCachedKeys.linkKey);
      // Filter actions to match cached keys, preserving order of cached keys
      const actions = currentCachedKeys.actionKeys
        .map(key => actionItems.find(i => i.key === key))
        .filter((i): i is ActionItem => !!i);

      // If we successfully recovered enough items, use them
      // (We check if we have at least some actions, though strict parity isn't required as we fill gaps later)
      if (actions.length > 0 || !currentCachedKeys.actionKeys.length) {
        return { link, actions, purpleActionKey: currentCachedKeys.purpleActionKey };
      }
    }

    // Otherwise (first run or cache invalid), randomize
    const shuffledLinks = [...linkItems].sort(() => Math.random() - 0.5);
    const shuffledActions = [...actionItems].sort(() => Math.random() - 0.5);

    const selectedLink = shuffledLinks[0];
    const selectedActions = shuffledActions; // We store all, but will slice later. 
    // Actually, let's store all keys so we can slice consistently later.

    // When no link item exists, designate the first shuffled action as the purple button
    const purpleActionKey = !selectedLink ? selectedActions[0]?.key : undefined;

    // Update cache
    cachedKeys = {
      linkKey: selectedLink?.key,
      actionKeys: selectedActions.map(i => i.key),
      purpleActionKey,
    };

    return { link: selectedLink, actions: selectedActions, purpleActionKey };
  });

  // Re-sync with pool if it changes (e.g. language change), using cached keys
  useEffect(() => {
    const currentCachedKeys = cachedKeys;
    if (!currentCachedKeys) return;

    const linkItems = pool.filter(i => i.kind === 'link');
    const actionItems = pool.filter(i => i.kind === 'action' && i.key !== 'act-find-sponsor-deals');

    const link = linkItems.find(i => i.key === currentCachedKeys.linkKey);
    const actions = currentCachedKeys.actionKeys
      .map(key => actionItems.find(i => i.key === key))
      .filter((i): i is ActionItem => !!i);

    setRandomizedItems({ link, actions, purpleActionKey: currentCachedKeys.purpleActionKey });
  }, [pool]);

  // Construct visible items based on randomized items and current state
  const visibleItems = useMemo(() => {
    // Desktop: always show exactly 4 buttons total
    // Mobile: show 3 buttons
    const totalTarget = isMobile ? 3 : 4;

    // If there are previous chats and we have the callback, include the green "Open last conversation" button
    const openLastItem: ActionItem | null = (onOpenLastConversation && hasPastConversations)
      ? { key: 'open-last-conversation', kind: 'open-last', label: t('chat.quick_actions.open_last_chat') }
      : null;

    // Ensure the sponsor deals action is always visible
    const sponsorItem = pool.find(i => i.key === 'act-find-sponsor-deals') as ActionItem | undefined;

    const pickedLink = randomizedItems.link ? [randomizedItems.link] : [];

    // Calculate how many items we need to pick from the pool
    // We guarantee: 1 sponsor item + 1 purple link + (openLastItem if exists)
    const guaranteedItems = (sponsorItem ? 1 : 0) + (pickedLink.length) + (openLastItem ? 1 : 0);
    const remaining = Math.max(0, totalTarget - guaranteedItems);

    // Pick remaining action items to fill up to totalTarget
    const pickedActions = randomizedItems.actions.slice(0, remaining);

    // Combine: openLastItem (if exists) + sponsor item (if exists) + one purple link + remaining actions
    // This ensures we always have exactly totalTarget items
    const combined = [
      ...(openLastItem ? [openLastItem] : []),
      ...(sponsorItem ? [sponsorItem] : []),
      ...pickedLink,
      ...pickedActions
    ];

    // If we don't have enough items, fill with more actions
    if (combined.length < totalTarget && randomizedItems.actions.length > pickedActions.length) {
      const additionalNeeded = totalTarget - combined.length;
      combined.push(...randomizedItems.actions.slice(pickedActions.length, pickedActions.length + additionalNeeded));
    }

    return combined.slice(0, totalTarget);
  }, [randomizedItems, isMobile, hasPastConversations, onOpenLastConversation, t, pool]);

  return (
    <div className="mt-4 mb-2 w-full px-2 sm:px-4 md:px-6 max-w-5xl xl:max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {visibleItems.map(item => {
          // Render green "Open last conversation" button when there are previous chats
          if (item.kind === 'open-last') {
            return (
              <button
                key={item.key}
                type="button"
                onClick={onOpenLastConversation}
                className="group w-full text-left p-4 backdrop-blur-md rounded-xl transition-all duration-200 text-sm whitespace-normal break-words
                           bg-green-300/5 border border-green-300/20 text-green-300
                           hover:bg-green-300/10 hover:border-green-300/60"
              >
                <div className="flex items-start leading-snug">
                  <span className="mr-3 text-xs text-green-300 group-hover:text-white">➤</span>
                  {item.label}
                </div>
              </button>
            );
          }

          // Render purple link buttons
          if (item.kind === 'link') {
            return (
              <Link
                key={item.key}
                href={item.href}
                className="group w-full text-left p-4 backdrop-blur-md rounded-xl transition-all duration-200 whitespace-normal break-words 
                           bg-purple-500/10 border border-purple-300/20 text-purple-200 text-sm
                           hover:bg-purple-500/20 hover:border-purple-300/40 hover:text-white"
              >
                <div className="flex items-start leading-snug">
                  <span className="text-purple-300 group-hover:text-white mr-3 text-xs">➤</span>
                  {item.label}
                </div>
              </Link>
            );
          }

          // Render regular action buttons (grey, or purple if designated)
          const isPurple = item.key === randomizedItems.purpleActionKey;
          return (
            <button
              key={item.key}
              onClick={() => onSelect(item.label)}
              className={isPurple
                ? "group w-full text-left p-4 backdrop-blur-md rounded-xl transition-all duration-200 text-sm whitespace-normal break-words bg-purple-500/10 border border-purple-300/20 text-purple-200 hover:bg-purple-500/20 hover:border-purple-300/40 hover:text-white"
                : "group w-full text-left p-4 backdrop-blur-md bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 hover:border-white/20 transition-all duration-200 text-neutral-300 hover:text-white text-sm whitespace-normal break-words"
              }
            >
              <div className="flex items-start leading-snug">
                <span className={`${isPurple ? "text-purple-300" : "text-neutral-500"} group-hover:text-white mr-3 text-xs`}>➤</span>
                {item.label}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders when props haven't changed
export const QuickActionButtons = React.memo(QuickActionButtonsComponent);
